import React from 'react';

const skeletonClass = 'animate-pulse bg-gray-200 dark:bg-gray-700';

/** Tek satır / çubuk iskelet */
export function SkeletonBar({
  className = '',
  width,
}: {
  className?: string;
  width?: string | number;
}) {
  return (
    <div
      className={`h-4 rounded ${skeletonClass} ${className}`}
      style={width != null ? { width: typeof width === 'number' ? `${width}px` : width } : undefined}
    />
  );
}

/** Yuvarlak iskelet (avatar vb.) */
export function SkeletonCircle({ className = '', size = 10 }: { className?: string; size?: number }) {
  return (
    <div
      className={`rounded-full ${skeletonClass} ${className}`}
      style={{ width: size * 4, height: size * 4 }}
    />
  );
}

/** Kart iskelet (başlık + birkaç satır) */
export function SkeletonCard({
  className = '',
  lines = 2,
  showIcon = true,
}: {
  className?: string;
  lines?: number;
  showIcon?: boolean;
}) {
  return (
    <div className={`rounded-xl border border-gray-200 dark:border-gray-700 p-4 ${className}`}>
      <div className="flex items-start gap-3">
        {showIcon && <SkeletonCircle size={10} className="shrink-0" />}
        <div className="min-w-0 flex-1 space-y-2">
          <SkeletonBar width="70%" />
          {Array.from({ length: lines - 1 }).map((_, i) => (
            <SkeletonBar key={i} width={i === 0 ? '90%' : '60%'} />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Tablo satırı iskelet (sütun sayısına göre) */
export function SkeletonTableRow({ cols = 5 }: { cols?: number }) {
  return (
    <tr className="border-b border-gray-100 dark:border-gray-700/50">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="py-3 px-4">
          <SkeletonBar width={i === cols - 1 ? 24 : i === 0 ? '80%' : '60%'} className="max-w-[8rem]" />
        </td>
      ))}
    </tr>
  );
}

/** Genel blok iskelet (başlık + içerik alanı) */
export function SkeletonBlock({ className = '', title = true }: { className?: string; title?: boolean }) {
  return (
    <div className={`rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden ${className}`}>
      {title && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <SkeletonBar width="40%" className="h-5" />
        </div>
      )}
      <div className="p-4 space-y-3">
        <SkeletonBar width="100%" />
        <SkeletonBar width="95%" />
        <SkeletonBar width="88%" />
      </div>
    </div>
  );
}

/** Detay sayfası iskelet: breadcrumb + başlık + 2 bilgi kartı */
export function SkeletonDetail() {
  return (
    <div className="p-4 space-y-6">
      <div className="flex items-center gap-2">
        <SkeletonBar width={64} className="h-4" />
      </div>
      <div>
        <SkeletonBar width="50%" className="h-7 mb-2" />
        <SkeletonBar width="30%" className="h-4" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <SkeletonBlock />
        <SkeletonBlock />
      </div>
      <SkeletonBlock title={true} />
    </div>
  );
}

const Skeleton = { SkeletonBar, SkeletonCircle, SkeletonCard, SkeletonTableRow, SkeletonBlock, SkeletonDetail };
export default Skeleton;
